/**
 * Pure helpers for the Workbench restore-event listener.
 *
 * The Workbench subscribes to a tenant-wide AppSync events channel that
 * publishes a ``FIELD_UPDATED`` beacon with
 * ``payload.changeKind === 'NETWORK_RESTORED'`` whenever any operator
 * (us *or* a collaborator) restores a network. Recipients are expected
 * to silently re-fetch the network so the agents dropdown / runtime
 * config / log level converge across all open editors.
 *
 * The listener has two non-trivial responsibilities:
 *
 *   1. Filter the incoming event buffer down to "restore beacons that
 *      apply to the network this user is currently viewing", deduped
 *      by ``eventId`` so React strict mode + the channel's hydration
 *      replay can't fire the refetch twice for the same publish.
 *
 *   2. Decide whether to also surface a low-priority shell toast — we
 *      only do that when the restore was triggered by *another*
 *      collaborator (so the local restore path stays single-toast).
 *
 * Both responsibilities are pure functions of (events, networkId, mySub,
 * seenSet), so they live here where Vitest can cover every shape
 * without booting a React tree or an AppSync mock.
 */

/**
 * Inspect the latest workbench-channel event buffer and decide what
 * the listener should do.
 *
 * @param {Array<object>} events  The full ``workbenchChannelEvents`` array.
 *   Append-only — the caller passes the same reference each tick.
 * @param {string} networkId      The network the user is currently viewing.
 *   Restore beacons targeting other networks are ignored.
 * @param {string} mySub          The current user's JWT sub. Used to
 *   classify a restore as "mine" vs "by another collaborator".
 * @param {Set<string>} seen      Mutable set of ``eventId``s the
 *   listener has already acted on. The function reads from it and
 *   returns the new ids it wants to add — the caller mutates ``seen``
 *   so the same publish can't trigger two refetches.
 * @returns {{ shouldRefetch: boolean, byOther: boolean, newSeenIds: Array<string> }}
 *   * ``shouldRefetch`` — call ``fetchNetwork(networkId, { silent: true })``.
 *   * ``byOther`` — show the "restored by another collaborator" toast.
 *   * ``newSeenIds`` — push these into the seen-set after acting.
 */
export function evaluateRestoreEvents(events, networkId, mySub, seen) {
  const result = {
    shouldRefetch: false,
    byOther: false,
    newSeenIds: [],
  };
  if (!Array.isArray(events) || events.length === 0) return result;
  if (!networkId) return result;

  const seenSet = seen instanceof Set ? seen : new Set();
  const me = String(mySub || '').trim();

  for (let i = 0; i < events.length; i += 1) {
    const evt = events[i] || {};
    const type = String(evt.eventType || '').toUpperCase();
    if (type !== 'FIELD_UPDATED') continue;

    const payload =
      evt.payload && typeof evt.payload === 'object' ? evt.payload : {};
    const changeKind = String(payload.changeKind || '').toUpperCase();
    if (changeKind !== 'NETWORK_RESTORED') continue;

    // The workbench channel is tenant-scoped, so it can multiplex
    // beacons for multiple networks. Pin the listener to the network
    // the user is actually viewing — restoring a sibling network in a
    // different browser tab must not yank the dropdown out from under
    // the user.
    const evtNetwork = String(
      evt.networkId || payload.networkId || '',
    ).trim();
    if (evtNetwork && evtNetwork !== networkId) continue;

    const eventId = String(evt.eventId || '').trim();
    if (eventId && seenSet.has(eventId)) continue;
    if (eventId) result.newSeenIds.push(eventId);

    result.shouldRefetch = true;

    const actor = String(evt.actorSub || payload.sub || '').trim();
    if (me && actor && actor !== me) {
      result.byOther = true;
    }
  }
  return result;
}
